/**
 * Actions for the lexicons module (VE-24948 / U5).
 *
 * Plain `createAction` rather than the `createGraphQLSuccessAction` helpers used
 * by `redactionCodes`: lexicon transport goes through an injected
 * `GraphQLExecutor` (see `services/executor.ts`) so the saga owns its own
 * loading and error state. Wrapping the transport in a second layer of
 * request/success/failure actions would duplicate that, and it is what makes
 * the fail-closed mutation sequence (ADR-003) testable as one unit rather than
 * as a chain of dispatches.
 */

import { createAction } from '@reduxjs/toolkit';

import {
  Lexicon,
  LexiconCsvValidationResult,
  LexiconEntryInput,
  LexiconSummary,
} from '../models';

const type = (name: string) => `LEXICONS/${name}`;

// --- Listing -----------------------------------------------------------------

export const FETCH_LEXICONS = createAction(type('FETCH_LEXICONS'));

/**
 * One page of the CURRENT view's list, plus what the page cannot see for
 * itself.
 *
 * `hasMore` is whether a page follows this one, measured by the query asking
 * for one record beyond the page. It replaces a `count` that looked like a
 * total and was not: the registry reports the size of the page it returned, so
 * the pager it fed disabled Next everywhere (VE-29495). `deletedCount` is the org's soft-deleted total,
 * capped at `DELETED_COUNT_MAX`, with `deletedCountExact` false at the cap; it
 * rides along because the live page holds none of those records.
 */
export const FETCH_LEXICONS_SUCCESS = createAction<{
  lexicons: readonly LexiconSummary[];
  hasMore: boolean;
  deletedCount: number;
  deletedCountExact: boolean;
}>(type('FETCH_LEXICONS_SUCCESS'));

/**
 * Re-reads the lexicon the drawer is editing, WITH its entries.
 *
 * The BR-6 conflict banner's Reload. It used to be `FETCH_LEXICONS`, because
 * the list carried entries and the reducer could rebuild the draft from it —
 * the list carries summaries now, so taking the other actor's version needs a
 * read of its own.
 */
/**
 * Downloads one lexicon as CSV.
 *
 * An action rather than a click handler because the row only has a SUMMARY —
 * the entries the file is made of have to be read first, and reading is a
 * saga's job.
 */
export const EXPORT_LEXICON = createAction<{ lexiconId: string }>(
  type('EXPORT_LEXICON')
);

export const RELOAD_EDITING_LEXICON = createAction(
  type('RELOAD_EDITING_LEXICON')
);

export const RELOAD_EDITING_LEXICON_SUCCESS = createAction<{
  lexicon: Lexicon;
}>(type('RELOAD_EDITING_LEXICON_SUCCESS'));

export const FETCH_LEXICONS_FAILURE = createAction<{ message: string }>(
  type('FETCH_LEXICONS_FAILURE')
);

/**
 * Lexicons are not provisioned in this environment (NFR-U5-12).
 *
 * A separate action from FAILURE on purpose: this is a legitimate state that
 * hides the feature, not an error to retry. GOV before its bootstrap runs is
 * exactly this.
 */
export const LEXICONS_NOT_CONFIGURED = createAction(
  type('LEXICONS_NOT_CONFIGURED')
);

export const SET_PAGE = createAction<{ offset: number; limit: number }>(
  type('SET_PAGE')
);

// --- Drawer / draft ----------------------------------------------------------

/** Opens the drawer. Omit `lexiconId` to create. */
export const OPEN_LEXICON_DRAWER = createAction<{ lexiconId?: string }>(
  type('OPEN_LEXICON_DRAWER')
);

export const CLOSE_LEXICON_DRAWER = createAction(type('CLOSE_LEXICON_DRAWER'));

export const SET_DRAFT_NAME = createAction<{ name: string }>(
  type('SET_DRAFT_NAME')
);

export const SET_DRAFT_NAME_ERROR = createAction<{ message?: string }>(
  type('SET_DRAFT_NAME_ERROR')
);

/**
 * Adds entries to the OPEN draft (not to the lexicon). Duplicates of entries
 * already in the draft are skipped, and the draft is capped at BR-16.
 */
export const ADD_DRAFT_ENTRIES = createAction<{
  entries: readonly LexiconEntryInput[];
}>(type('ADD_DRAFT_ENTRIES'));

/**
 * Replaces one entry in the OPEN draft, IN PLACE.
 *
 * In place is the point: the entry keeps its id and its position, so an edit
 * reads as an edit rather than as a delete followed by an append that jumps the
 * row to the bottom of the list. `selectDraftDiff` compares the result against
 * the open-time baseline and reports it as `updated`.
 */
export const UPDATE_DRAFT_ENTRY = createAction<{
  entryId: string;
  entry: LexiconEntryInput;
}>(type('UPDATE_DRAFT_ENTRY'));

/**
 * Deletes one entry from the OPEN draft.
 *
 * SOFT for an entry that has been saved before — it is tombstoned with
 * `isDeleted` and stays in the draft, so the drawer can offer it back. The
 * delete control is an unguarded icon on every row and losing a term to one
 * stray click is otherwise silent.
 *
 * HARD for an entry that only exists in this draft (a `draft:` id). There is
 * nothing to recover — it was never saved — and a tombstone for it would be
 * noise in the deleted view and an item against the schema's `maxItems`.
 */
export const REMOVE_DRAFT_ENTRY = createAction<{ entryId: string }>(
  type('REMOVE_DRAFT_ENTRY')
);

/** Clears the tombstone on one entry, putting it back in the live list. */
export const RESTORE_DRAFT_ENTRY = createAction<{ entryId: string }>(
  type('RESTORE_DRAFT_ENTRY')
);

/** Drops ONE tombstone for good, from the deleted view's own trash control. */
export const PURGE_DRAFT_ENTRY = createAction<{ entryId: string }>(
  type('PURGE_DRAFT_ENTRY')
);

/**
 * Drops every tombstone in the draft for good.
 *
 * The only thing that reclaims room under the schema's `maxItems: 5000`, which
 * counts tombstones like any other item. Irreversible, so the drawer puts a
 * confirmation in front of it — unlike the per-row delete, which is exactly the
 * unguarded control this whole mechanism exists to make survivable.
 */
export const PURGE_DELETED_DRAFT_ENTRIES = createAction(
  type('PURGE_DELETED_DRAFT_ENTRIES')
);

/**
 * Commits the open draft in one Save: create (name + entries) when the draft
 * has no lexicon yet, otherwise rename / append / remove as needed — each
 * step its own audited mutation (ADR-003). The saga reads the draft from the
 * store, so the payload is empty.
 */
export const SAVE_LEXICON_DRAFT = createAction(type('SAVE_LEXICON_DRAFT'));

/** Reported by the service when a write lost a revision race (BR-6). */
export const SET_LEXICON_CONFLICT = createAction<{
  modifiedBy: string;
  modifiedDateTime: string;
}>(type('SET_LEXICON_CONFLICT'));

export const CLEAR_LEXICON_CONFLICT = createAction(
  type('CLEAR_LEXICON_CONFLICT')
);

// --- Mutations ---------------------------------------------------------------

export const CREATE_LEXICON = createAction<{ name: string }>(
  type('CREATE_LEXICON')
);

export const RENAME_LEXICON = createAction<{
  lexiconId: string;
  name: string;
}>(type('RENAME_LEXICON'));

export const ADD_LEXICON_ENTRIES = createAction<{
  lexiconId: string;
  entries: readonly LexiconEntryInput[];
}>(type('ADD_LEXICON_ENTRIES'));

export const REMOVE_LEXICON_ENTRIES = createAction<{
  lexiconId: string;
  entryIds: readonly string[];
}>(type('REMOVE_LEXICON_ENTRIES'));

/**
 * Flips a library flag from the settings table.
 *
 * ONE action for both checkboxes rather than one each, because they are one
 * write: a partial payload says which flags this change touches, and the
 * service leaves the others exactly as it found them. The alternative — two
 * actions, two sagas, two near-identical writes — is how the watermark module
 * ended up with `SET_WATERMARK_ENABLED` and `SET_WATERMARK_AUTO_APPLY` doing
 * the same thing twice.
 *
 * The reducer applies it OPTIMISTICALLY. A checkbox that waits for a round trip
 * before moving reads as broken, and a failure re-fetches the page, so the
 * worst case is a tick that flicks back with a notice beside it.
 */
export const SET_LEXICON_FLAGS = createAction<{
  lexiconId: string;
  flags: { enabled?: boolean; autoApply?: boolean };
}>(type('SET_LEXICON_FLAGS'));

/**
 * The table's Delete — a SOFT delete since BR-5 was amended.
 *
 * The name is unchanged on purpose: from the user's side this is still "delete
 * the lexicon", and every call site, the confirm dialog and the audit kind
 * (`lexicon.delete`) mean the same thing they always did. What changed is what
 * the registry is asked to do — raise `isDeleted` rather than remove records.
 */
export const DELETE_LEXICON = createAction<{ lexiconId: string }>(
  type('DELETE_LEXICON')
);

/** Undoes a soft delete. Refused when a live lexicon has taken the name. */
export const RESTORE_LEXICON = createAction<{ lexiconId: string }>(
  type('RESTORE_LEXICON')
);

/**
 * PERMANENT delete, from the deleted view only.
 *
 * The one irreversible operation in the feature, and the only action whose name
 * says so. Both records go — the summary and its entries.
 */
export const PURGE_LEXICON = createAction<{ lexiconId: string }>(
  type('PURGE_LEXICON')
);

/**
 * Which of the two lists the settings page is showing.
 *
 * STORE state, not the table's own, for two reasons. The page header depends on
 * it — the title names the view and Create New is hidden while the deleted list
 * is up — and so does the FETCH: the two lists are two filtered queries, and
 * the saga reads this to decide which filter to send. It is dispatched with a
 * `FETCH_LEXICONS` behind it for that reason.
 */
export const SET_SHOW_DELETED_LEXICONS = createAction<{ show: boolean }>(
  type('SET_SHOW_DELETED_LEXICONS')
);

export const LEXICON_MUTATION_SUCCESS = createAction<{ lexicon?: Lexicon }>(
  type('LEXICON_MUTATION_SUCCESS')
);

export const LEXICON_DELETED = createAction<{ lexiconId: string }>(
  type('LEXICON_DELETED')
);

/**
 * A mutation was refused because it could not be audited (NFR-S4).
 *
 * Distinct from a generic failure so the UI can say "not applied" rather than
 * leaving the user guessing whether it partly happened. Under SEC-R4 that
 * distinction is the whole point of failing closed.
 */
export const LEXICON_MUTATION_BLOCKED_BY_AUDIT = createAction<{
  message: string;
}>(type('LEXICON_MUTATION_BLOCKED_BY_AUDIT'));

/**
 * The write succeeded but its audit `outcome` event did not (ADR-003).
 *
 * The change DID happen, so this is not a failure — but the trail shows an
 * intent with no outcome and a reviewer may need to resolve it.
 */
export const LEXICON_MUTATION_AUDIT_INCOMPLETE = createAction<{
  operationId: string;
  message: string;
}>(type('LEXICON_MUTATION_AUDIT_INCOMPLETE'));

export const LEXICON_MUTATION_FAILURE = createAction<{ message: string }>(
  type('LEXICON_MUTATION_FAILURE')
);

/**
 * A mutation has finished, whatever the outcome.
 *
 * Clears `isMutating` and NOTHING else — a notice already set by
 * `LEXICON_MUTATION_FAILURE` / `_BLOCKED_BY_AUDIT` / `_AUDIT_INCOMPLETE` must
 * survive it, so this is dispatched last, from a `finally`.
 *
 * WHY IT EXISTS. Six mutation actions set `isMutating: true` in the reducer and
 * every one of them relied on a saga reaching one of the outcome actions to
 * clear it. Two shapes broke that: `getSagaContext` was called OUTSIDE each
 * saga's `try`, so a failure to resolve the lexicon schema or the auth context
 * cleared nothing, and `onSaveLexiconDraft` had an early `return` that
 * dispatched nothing at all. The result was a page where every mutation
 * control stayed disabled until reload — including the CSV dialog's Import
 * button, which is gated on `isMutating` and has nothing to do with the
 * mutation that got stuck.
 */
export const LEXICON_MUTATION_SETTLED = createAction(
  type('LEXICON_MUTATION_SETTLED')
);

/** Dismisses the mutation outcome notice the page is showing. */
export const CLEAR_MUTATION_NOTICE = createAction(
  type('CLEAR_MUTATION_NOTICE')
);

// --- CSV import --------------------------------------------------------------

export const START_CSV_IMPORT = createAction<{
  /** Absent while the lexicon is still being created (draft-only import). */
  lexiconId?: string;
  fileName: string;
  rowCount: number;
}>(type('START_CSV_IMPORT'));

export const SET_CSV_IMPORT_RESULT = createAction<{
  result: LexiconCsvValidationResult;
}>(type('SET_CSV_IMPORT_RESULT'));

export const CANCEL_CSV_IMPORT = createAction(type('CANCEL_CSV_IMPORT'));
